import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

export function useRegisterDeletionPreview(databaseName, enabled) {
  const [state, setState] = useState({});
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!enabled || !databaseName) { setState({}); return undefined; }
    let cancelled = false;
    setState({ name: databaseName, loading: true });
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/database/${encodeURIComponent(databaseName)}/deletion-preview`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then(({ data }) => {
      if (!data?.deletionToken || data.databaseName !== databaseName || !Array.isArray(data.users)) throw new Error('Invalid deletion preview');
      if (!cancelled) setState({ name: databaseName, preview: data });
    }).catch(error => {
      if (!cancelled) setState({ name: databaseName, error: error.response?.data?.message || 'Could not load the deletion preview. Retry before deleting.' });
    });
    return () => { cancelled = true; };
  }, [databaseName, enabled, revision]);
  const current = enabled && state.name === databaseName ? state : {};
  return { ...current, reload: () => { setState({}); setRevision(value => value + 1); } };
}

export default function RegisterDeletionPreview({ state }) {
  const { preview, error } = state;
  if (error) return <Alert severity="error" action={<Button color="inherit" onClick={state.reload}>Retry</Button>}>{error}</Alert>;
  if (!preview) return <Box role="status" sx={{ my: 2 }}><CircularProgress size={18} /> Loading deletion preview…</Box>;
  return <Box sx={{ my: 2 }}>
    <Alert severity="error">This permanently deletes the register, its equipment and inspection data, photos, and uploaded branding. Existing backups are retained.</Alert>
    <Alert severity="info" sx={{ mt: 1 }}>User accounts and access to other registers are preserved. Users currently viewing this register must choose another available register; accounts with no other register will have no register access.</Alert>
    {preview.phase !== 'preview' && <Alert severity="warning" sx={{ mt: 1 }}>{preview.phase === 'complete' ? 'This deletion is already complete.' : 'Deletion has started. Confirm again to finish the remaining cleanup.'}</Alert>}
    <Typography sx={{ mt: 1.5 }} variant="body2">{preview.users.length} affected accounts · {preview.memberships} active memberships · {preview.seats} allocated seats · {preview.invitations} pending member invitations · {preview.api_keys} active API keys · {preview.trial_invites} trial invitations</Typography>
    {!!preview.users.length && <Box component="ul" sx={{ pl: 2.5, maxHeight: 160, overflow: 'auto', my: 1 }}>
      {preview.users.map(user => <Typography component="li" variant="body2" key={user.id}>{user.username} — {user.role === 'admin' ? 'platform administrator access retained' : `${user.other_registers} other register memberships retained`}</Typography>)}
    </Box>}
  </Box>;
}
